import React, { useState } from "react";
import { useQuery } from "react-query";
import apiClient from "../../../services/apiClient";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  CircularProgress,
  Container,
  Box,
  Paper,
  Typography,
} from "@mui/material";

import { BaseGrid, NotificationAlert, Title } from "../../index";
import { format, parseISO } from "date-fns";
import { frCA } from "date-fns/locale";

function TwilioLogs() {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [messages, setMessages] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchPhone, setSearchPhone] = useState("");

  // Date de début : 7 derniers jours
  const today = new Date();
  const threeDaysAgo = new Date(today);
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
  threeDaysAgo.setHours(0, 0, 0, 0); // Début de la journée

  // Fonction pour formater le numéro de téléphone (enlever les espaces, tirets, etc.)
  const normalizePhoneNumber = (phone) => {
    if (!phone) return "";
    // Garder seulement les chiffres et le +
    return phone.replace(/[^\d+]/g, "");
  };

  // Récupérer les messages inbound (réponses reçues) des 7 derniers jours
  const { isLoading } = useQuery(
    ["fetchTwilioSMSLogs", searchPhone],
    async () => {
      const params = {
        dateSentAfter: threeDaysAgo.toISOString(),
        dateSentBefore: today.toISOString(),
        // Pas de limite : le backend pagine automatiquement pour récupérer tous les messages
      };

      // Si un numéro est recherché, filtrer par ce numéro
      if (searchPhone) {
        const normalizedPhone = normalizePhoneNumber(searchPhone);
        // Rechercher dans les messages où le numéro est soit l'expéditeur (from) soit le destinataire (to)
        // Pour les réponses, on cherche dans "from" car c'est le client qui répond
        params.from = normalizedPhone;
      }

      const response = await apiClient.fetchSMSLogs(params);
      return response;
    },
    {
      enabled: true,
      onSuccess: ({ data }) => {
        // Filtrer uniquement les messages inbound (réponses reçues)
        const inboundMessages = (data?.data || []).filter(
          (msg) => msg.direction === "inbound"
        );
        // Trier par date (plus récent en premier)
        inboundMessages.sort((a, b) => {
          const dateA = new Date(a.dateSent || a.dateCreated);
          const dateB = new Date(b.dateSent || b.dateCreated);
          return dateB - dateA;
        });
        setMessages(inboundMessages);
      },
      onError: (error) => {
        console.error("Erreur lors de la récupération des messages:", error);
        setErrors({ msg: true });
      },
    }
  );

  const gridColumns = [
    {
      name: "dateSent",
      label: "Date",
      options: {
        customBodyRender: (value) => {
          if (!value) return "-";
          try {
            const date = parseISO(value);
            return format(date, "PPpp", { locale: frCA });
          } catch (e) {
            return value;
          }
        },
      },
    },
    {
      name: "from",
      label: "De",
      options: {
        customBodyRender: (value) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">{value || "-"}</Typography>
          </Box>
        ),
      },
    },
    {
      name: "body",
      label: "Message",
      options: {
        customBodyRender: (value) => (
          <Box
            sx={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              maxWidth: "none",
            }}
          >
            {value || "-"}
          </Box>
        ),
      },
    },
  ];

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="secondary" size={40} />
      </Backdrop>
      <NotificationAlert
        openNotification={errors?.msg}
        type="error"
        message={
          t("twilioLogs.ErrorFetchingData") ||
          "Erreur lors de la récupération des messages"
        }
      />
      <Container maxWidth="xl" sx={{ pt: 5, pb: 5 }}>
        <Title
          handleAdd=""
          pageTitle="Messages reçus (Twilio)"
          iconTooltip=""
          noAdd={true}
        />

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {messages.length} message(s) trouvé(s)
            </Typography>
          </Box>
        </Paper>

        {messages.length > 0 ? (
          <BaseGrid gridData={messages} gridColumn={gridColumns} />
        ) : (
          !isLoading && (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                {searchPhone
                  ? `Aucun message trouvé pour le numéro: ${searchPhone}`
                  : "Aucun message reçu dans les 3 derniers jours"}
              </Typography>
            </Paper>
          )
        )}
      </Container>
    </>
  );
}

export default TwilioLogs;
